import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { device } from "../../../../../constants";
import { cn } from "../../../../../lib/utils";
import { useProducts } from "../../hooks/useProducts";

const ASSETS_PATH = "/landings/assets/images/products/hub-talentos";

const Section = styled.div`
	width: 100%;
	--height-section-height: 783px;
	height: 100vh;
	min-height: var(--height-section-height);
	max-height: var(--height-section-height);
	overflow: hidden;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: var(--height-section-height);
		background: linear-gradient(
			to bottom,
			rgba(0, 0, 0, 0) 70.37%,
			#000 100%
		);
		z-index: 1;
	}

	@media ${device.tablet} {
		--height-section-height: 437px;
		&::before {
			background: linear-gradient(
					90deg,
					rgba(0, 0, 0, 0.85) 0%,
					rgba(0, 0, 0, 0.4) 50%,
					rgba(0, 0, 0, 0) 75%
				);
		}
	}

	@media ${device.laptop} {
		--height-section-height: 700px;
	}

	@media ${device.laptopM} {
		--height-section-height: 768px;
		&::before {
			background: linear-gradient(
					269.65deg,
					rgba(21, 25, 30, 0) 27.6%,
					rgba(21, 25, 30, 0.605248) 50.48%,
					#15191e 98.95%
				),
				linear-gradient(0deg, rgba(21, 25, 30, 0.15), rgba(21, 25, 30, 0.15));
		}
	}
`;

export default function HeroSection() {
	const { scrollToContactForm } = useProducts();
	const { t } = useTranslation();

	return (
		<Section>
			<section
				className={cn(
					"relative bg-neutral-08",
					"mt-[80px] h-[703px]",
					"tablet:h-[357px]",
					"laptop:h-[620px]",
					"tabletL:mt-0",
					"laptopM:h-[768px]",
				)}
			>
				<img
					src={`${ASSETS_PATH}/hero-bg-mobile.png`}
					alt={t("products.hub-talentos.hero.imageAlt")}
					className="absolute top-0 left-0 z-0 object-cover w-full h-full tablet:hidden"
				/>
				<img
					src={`${ASSETS_PATH}/hero-bg.jpg`}
					alt={t("products.hub-talentos.hero.imageAlt")}
					className="hidden absolute top-0 left-0 z-0 object-cover w-full h-full tablet:block"
				/>

				<div
					className={cn(
						"h-full",
						"pt-10 px-5 pb-10",
						"tablet:pt-[40px] tablet:px-[40px] tablet:pb-[40px]",
						"laptop:pt-[120px] laptop:px-[80px] laptop:pb-[80px]",
						"laptopM:pt-[200px] laptopM:pl-[80px] laptopM:pb-[80px]",
					)}
				>
					<div className="z-10 relative h-full flex flex-col justify-start tablet:justify-start laptopM:justify-start">
						<h1
							className={cn(
								"uppercase w-full mb-0 ml-0 mr-0 mt-0",
								"text-[24px] leading-[32px]",
								"tablet:text-[32px] tablet:leading-[40px]",
								"laptop:text-[44px] laptop:leading-[52px]",
								"laptopM:text-[56px] laptopM:leading-[64px]",
								"font-bold !text-vivid-02",
							)}
						>
							{t("products.hub-talentos.hero.title")}
						</h1>

						<div
							className={cn(
								"mt-[16px] max-w-full",
								"tablet:max-w-[419px]",
								"laptop:max-w-[540px]",
								"laptopM:max-w-[715px]",
							)}
						>
							<p
								className={cn(
									"!text-white tracking-[1.2px] mb-0 ml-0 mr-0 mt-0",
									"font-medium",
									"text-base leading-7",
									"tablet:text-lg tablet:leading-6",
									"laptop:text-2xl laptop:leading-9 laptop:tracking-[1.6px]",
									"laptopM:text-[32px] laptopM:leading-[40px]",
								)}
							>
								<span className="tablet:hidden !text-white">
									{t("products.hub-talentos.hero.description1MobileLine1")}
									<br />
									{t("products.hub-talentos.hero.description1MobileLine2")}
								</span>
								<span className="hidden tablet:inline !text-white">
									{t("products.hub-talentos.hero.description1")}
								</span>
							</p>
							<p
								className={cn(
									"!text-ds-brand-green-300 tracking-[1.2px] mb-0 ml-0 mr-0 mt-0",
									"font-semibold",
									"text-base leading-7",
									"tablet:text-lg tablet:leading-6",
									"laptop:text-2xl laptop:leading-9 laptop:tracking-[1.6px]",
									"laptopM:text-[32px] laptopM:leading-[40px]",
								)}
							>
								<span className="tablet:hidden !text-ds-brand-green-300">
									{t("products.hub-talentos.hero.description2MobileLine1")}
									<br />
									{t("products.hub-talentos.hero.description2MobileLine2")}
								</span>
								<span className="hidden tablet:inline !text-ds-brand-green-300">
									{t("products.hub-talentos.hero.description2")}
								</span>
							</p>
						</div>

						<div className="mt-auto tablet:mt-8 laptopM:mt-8 flex justify-center tablet:justify-start">
							<button
								onClick={scrollToContactForm}
								className={cn(
									"bg-vivid-02 rounded-full border-0 cursor-pointer",
									"py-3 px-6",
									"tablet:py-0 tablet:h-[40px] tablet:px-6",
									"laptop:h-[56px] laptop:px-8",
									"laptopM:h-[64px] laptopM:px-12",
									"flex items-center justify-center",
									"hover:opacity-90 transition-opacity",
								)}
							>
								<span
									className={cn(
										"!text-ds-brand-green-1000 font-bold uppercase whitespace-nowrap",
										"text-sm tracking-[1.2px] leading-[24px]",
										"tablet:text-sm",
										"laptop:text-base",
										"laptopM:text-xl laptopM:leading-[40px]",
									)}
								>
									{t("products.hub-talentos.hero.ctaButton")}
								</span>
							</button>
						</div>
					</div>
				</div>

			</section>
		</Section>
	);
}
